/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import styles from './serviceProviderRegistration.module.scss';
import FormBox from '../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import api from 'api';
import { FaTrash } from 'react-icons/fa';
import { Autocomplete, TextField } from '@mui/material';
import BaseInput from 'components/common/BaseInput';
import ToggleButtonGroup from '../common/ToggleButtonGroup/ToggleButtonGroup';
import { ADDITIONAL_CONSULTANT_ROLES } from 'data/constants/roles';
import ReactGA from 'react-ga4';

export interface Agent {
    firstname: string;
    lastname: string;
    middlename: string;
    role: string;
    email: string;
}

export const initAgent: Agent = {
    firstname: '',
    lastname: '',
    middlename: '',
    role: '',
    email: '',
};

interface ValidationMsgAgent {
    firstname: string;
    lastname: string;
    role: string;
    email: string;
}

const initMsgAgent: ValidationMsgAgent = {
    firstname: '',
    lastname: '',
    role: '',
    email: '',
};

export default function Consultants() {
    const tableName = 'ImmigrationProfessional';
    const [agents, setAgents] = useState<Agent[]>([]);
    const [validationAgents, setValidationAgents] = useState<ValidationMsgAgent[]>([]);

    const loadAgents = (data: Agent[]) => {
        const tmp = new Array(data.length).fill(initMsgAgent);
        setOtherProfessionals('Yes');
        setValidationAgents(tmp);
        setAgents(data as Agent[]);
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'Consultants',
        });
    }, []);

    useEffect(() => {
        api.tableInput.newFetchCandidateInput(loadAgents, tableName);
    }, []);

    // FIELD VALIDATION
    const validate = (idx: number, field: string, value: string) => {
        let validated = true;

        const tmp = [...validationAgents];
        if (!field && !value) {
            for (let i = 0; i < agents.length; i++) {
                for (const [key, value] of Object.entries(agents[i])) {
                    if (key !== 'middlename') {
                        let msg = '';
                        if (value === '') {
                            msg = 'Field should not be left empty';
                            validated = false;
                            tmp[i] = { ...tmp[i], [key]: msg };
                            setValidationAgents(tmp);
                        } else if (key === 'email') {
                            if (!validateEmail(idx, key, value)) {
                                msg = 'Invalid email address';
                                tmp[i] = { ...tmp[i], [key]: msg };
                                setValidationAgents(tmp);
                                validated = false;
                            }
                        }
                    }
                }
            }
        } else {
            let msg = '';
            if (value === '') {
                msg = 'Field should not be left empty';
                validated = false;
            }
            tmp[idx] = { ...tmp[idx], [field]: msg };
        }
        setValidationAgents(tmp);

        return validated;
    };

    const validateEmail = (idx: number, field: string, value: string) => {
        handleUpdateAgent(idx, field, value);
        let validated = true;
        let msg = '';
        const tmp = [...validationAgents];

        if (!value) {
            msg = 'Field should not be left empty';
            validated = false;
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
            msg = 'Invalid email address';
            validated = false;
        }
        tmp[idx] = { ...tmp[idx], [field]: msg };

        setValidationAgents(tmp);
        return validated;
    };

    const handleUpdateAgent = (idx: number, field: string, value: string) => {
        validate(idx, field, value);
        const temp = [...agents];
        temp[idx] = { ...agents[idx], [field]: value };
        setAgents(temp);
    };

    const handleAddAgent = () => {
        const temp: Agent[] = [...agents, initAgent];
        setAgents(temp);
        const tempValidation: ValidationMsgAgent[] = [...validationAgents, initMsgAgent];
        setValidationAgents(tempValidation);
    };

    const handleDeleteAgent = (idx: number) => {
        const temp: Agent[] = [...agents];
        temp.splice(idx, 1);
        setAgents(temp);

        const tempMsg: ValidationMsgAgent[] = [...validationAgents];
        tempMsg.splice(idx, 1);
        setValidationAgents(tempMsg);
    };

    const [otherProfessionals, setOtherProfessionals] = useState<string>(
        agents.length ? 'Yes' : 'No'
    );

    const handleAddProfessionals = (value: string) => {
        if (value === 'Yes') {
            setOtherProfessionals('Yes');
            setAgents([initAgent]);
            setValidationAgents([initMsgAgent]);
        } else if (value === 'No') {
            setOtherProfessionals('No');
            setAgents([]);
            setValidationAgents([]);
        }
    };

    return (
        <FormBox validation={validate} tableNames={[tableName]} data={[agents]}>
            <p className="form-card-title mb-20">Additional Consultants In Your Company</p>

            <ToggleButtonGroup
                options={['Yes', 'No']}
                name=""
                label={
                    'Would you like to register other immigration professionals under your company?'
                }
                value={otherProfessionals}
                onChange={(_: string, value: string) => handleAddProfessionals(value)}
            />
            {otherProfessionals === 'Yes' && (
                <>
                    {agents.map((f, i) => {
                        return (
                            <div key={i}>
                                <div>
                                    <div className="form-card-content-divider"></div>
                                    <div className="form-card-content-title">
                                        <>Immigration professional {i + 1}</>
                                        <div
                                            className="delete-action"
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            <button
                                                onClick={() => {
                                                    handleDeleteAgent(i);
                                                }}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-card-content family">
                                    <div className="grid-section d-grid-2">
                                        <BaseInput
                                            id="firstName"
                                            label="First Name"
                                            type="text"
                                            value={agents[i].firstname}
                                            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                                validate(i, 'firstname', e.target.value);
                                            }}
                                            onChange={(e) => {
                                                handleUpdateAgent(i, 'firstname', e.target.value);
                                            }}
                                            error={validationAgents[i].firstname !== ''}
                                            helperText={validationAgents[i].firstname}
                                        />
                                        <BaseInput
                                            id="lastName"
                                            label="Last Name"
                                            type="text"
                                            value={agents[i].lastname}
                                            onChange={(e) => {
                                                handleUpdateAgent(i, 'lastname', e.target.value);
                                            }}
                                            error={validationAgents[i].lastname !== ''}
                                            helperText={validationAgents[i].lastname}
                                        />
                                        <BaseInput
                                            id="middleName"
                                            label="Middle Name"
                                            type="text"
                                            value={agents[i].middlename}
                                            onChange={(e) => {
                                                handleUpdateAgent(i, 'middlename', e.target.value);
                                            }}
                                        />
                                        <BaseInput
                                            id="agent_email"
                                            label="Email"
                                            type="text"
                                            value={agents[i].email}
                                            onFocus={(e) => {
                                                validate(i, 'email', e.target.value);
                                            }}
                                            onChange={(e) => {
                                                handleUpdateAgent(i, 'email', e.target.value);
                                            }}
                                            error={validationAgents[i].email !== ''}
                                            helperText={validationAgents[i].email}
                                        />

                                        <Autocomplete
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    height: 30,
                                                },
                                            }}
                                            disablePortal
                                            value={agents[i].role}
                                            id="role"
                                            options={ADDITIONAL_CONSULTANT_ROLES}
                                            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                                validate(i, 'role', e.target.value);
                                            }}
                                            onChange={(_, value: any) =>
                                                handleUpdateAgent(i, 'role', value)
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Role"
                                                    error={validationAgents[i].role !== ''}
                                                    helperText={validationAgents[i].role}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div className="form-footer">
                        <div className="footer-title"> Add Another Consultant</div>
                        <div className="footer-actions">
                            <button
                                className={`footer-action-btn ${styles.button}`}
                                onClick={() => handleAddAgent()}
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </>
            )}
        </FormBox>
    );
}
